import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, User, Users, Stethoscope, HelpCircle,
  CheckCircle, Phone, Calendar, ShieldCheck, FileText, AlertCircle, MapPin
} from 'lucide-react';
import { siteConfig } from '../data/content';
import BookingModal from '../components/common/BookingModal';

const StartHerePage = () => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    userType: '',
    lookingFor: '',
    insurance: '',
    referral: '',
    visitPreference: ''
  });
  const [showBookingModal, setShowBookingModal] = useState(false);

  const updateAnswer = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
    // Auto advance to next step
    if (step < 5) {
      setTimeout(() => setStep(step + 1), 300);
    }
  };

  const goBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const resetForm = () => {
    setStep(1);
    setAnswers({
      userType: '',
      lookingFor: '',
      insurance: '',
      referral: '',
      visitPreference: ''
    });
  };

  const OptionCard = ({ icon: Icon, title, description, value, fieldKey, testId }) => (
    <button
      onClick={() => updateAnswer(fieldKey, value)}
      className={`w-full p-4 md:p-6 text-left rounded-xl border-2 transition-all duration-200 
        ${answers[fieldKey] === value 
          ? 'border-teal-600 bg-teal-50 ring-2 ring-teal-200' 
          : 'border-slate-200 bg-white hover:border-teal-300 hover:bg-slate-50'
        }`}
      data-testid={testId}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0
          ${answers[fieldKey] === value ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
          <Icon size={24} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 mb-1">{title}</h3>
          {description && <p className="text-sm text-slate-600">{description}</p>}
        </div>
        {answers[fieldKey] === value && (
          <CheckCircle size={20} className="text-teal-600 flex-shrink-0" />
        )}
      </div>
    </button>
  );

  const ProgressBar = () => (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-slate-600">Step {step} of 5</span>
        <span className="text-sm text-slate-500">{Math.round((step / 5) * 100)}% complete</span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-teal-600 transition-all duration-500 ease-out"
          style={{ width: `${(step / 5) * 100}%` }}
        />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="pt-32 pb-8 bg-gradient-to-b from-teal-50 to-slate-50">
        <div className="container">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 mb-6"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          <h1 className="heading-1 mb-2" data-testid="start-here-title">Start Here</h1>
          <p className="body-large max-w-2xl">
            Answer a few quick questions so we can guide you to the right next step.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 md:py-12">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">
              <ProgressBar />

              {/* Step 1: Who are you? */}
              {step === 1 && (
                <div className="animate-fadeIn">
                  <h2 className="heading-2 mb-2">Who are you?</h2>
                  <p className="body-medium text-slate-600 mb-6">
                    Select the option that best describes you.
                  </p>
                  <div className="space-y-4">
                    <OptionCard
                      icon={User}
                      title="I'm a Patient"
                      description="I'm looking for therapy services for myself"
                      value="patient"
                      fieldKey="userType"
                      testId="option-patient"
                    />
                    <OptionCard
                      icon={Users}
                      title="I'm a Caregiver"
                      description="I'm helping a family member or loved one"
                      value="caregiver"
                      fieldKey="userType"
                      testId="option-caregiver"
                    />
                    <OptionCard
                      icon={Stethoscope}
                      title="I'm a Healthcare Provider"
                      description="I want to refer a patient"
                      value="provider"
                      fieldKey="userType"
                      testId="option-provider"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: What are you looking for? */}
              {step === 2 && (
                <div className="animate-fadeIn">
                  <h2 className="heading-2 mb-2">What are you looking for?</h2>
                  <p className="body-medium text-slate-600 mb-6">
                    Select the service that best matches your needs.
                  </p>
                  <div className="space-y-3">
                    {[
                      { value: 'swallowing_eval', title: 'Swallowing Evaluation', desc: 'Assessment for swallowing difficulties (dysphagia)' },
                      { value: 'dysphagia_therapy', title: 'Dysphagia Therapy', desc: 'Treatment for swallowing disorders' },
                      { value: 'speech_language', title: 'Speech-Language Therapy', desc: 'Help with communication and language' },
                      { value: 'cognitive_comm', title: 'Cognitive-Communication Therapy', desc: 'For memory, attention, and problem-solving' },
                      { value: 'voice_therapy', title: 'Voice Therapy', desc: 'Treatment for voice disorders' },
                      { value: 'caregiver_education', title: 'Caregiver Education', desc: 'Learn how to support your loved one' },
                      { value: 'not_sure', title: "I'm Not Sure", desc: 'I need help figuring out what I need' }
                    ].map(option => (
                      <OptionCard
                        key={option.value}
                        icon={option.value === 'not_sure' ? HelpCircle : CheckCircle}
                        title={option.title}
                        description={option.desc}
                        value={option.value}
                        fieldKey="lookingFor"
                        testId={`option-${option.value.replace('_', '-')}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Insurance */}
              {step === 3 && (
                <div className="animate-fadeIn">
                  <h2 className="heading-2 mb-2">How will you pay?</h2>
                  <p className="body-medium text-slate-600 mb-6">
                    Select your payment preference.
                  </p>
                  <div className="space-y-4">
                    <OptionCard
                      icon={ShieldCheck}
                      title="I Have Insurance"
                      description="I'd like to use my health insurance"
                      value="insurance"
                      fieldKey="insurance"
                      testId="option-insurance"
                    />
                    <OptionCard
                      icon={FileText}
                      title="Self-Pay"
                      description="I'll pay out of pocket"
                      value="self_pay"
                      fieldKey="insurance"
                      testId="option-self-pay"
                    />
                    <OptionCard
                      icon={HelpCircle}
                      title="I'm Not Sure"
                      description="I need help understanding my options"
                      value="not_sure"
                      fieldKey="insurance"
                      testId="option-insurance-unsure"
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Referral */}
              {step === 4 && (
                <div className="animate-fadeIn">
                  <h2 className="heading-2 mb-2">Do you have a physician referral?</h2>
                  <p className="body-medium text-slate-600 mb-6">
                    Some insurance plans require a referral from your doctor.
                  </p>
                  <div className="space-y-4">
                    <OptionCard
                      icon={CheckCircle}
                      title="Yes, I Have a Referral"
                      description="My doctor has referred me for therapy"
                      value="yes"
                      fieldKey="referral"
                      testId="option-referral-yes"
                    />
                    <OptionCard
                      icon={Phone}
                      title="No, I Need to Get One"
                      description="I need to request a referral from my doctor"
                      value="need_one"
                      fieldKey="referral"
                      testId="option-referral-need"
                    />
                    <OptionCard
                      icon={HelpCircle}
                      title="I Don't Know"
                      description="I'm not sure if I need one"
                      value="not_sure"
                      fieldKey="referral"
                      testId="option-referral-unsure"
                    />
                  </div>
                </div>
              )}

              {/* Step 5: Service Area / Location */}
              {step === 5 && (
                <div className="animate-fadeIn">
                  <h2 className="heading-2 mb-2">Where are you located?</h2>
                  <p className="body-medium text-slate-600 mb-6">
                    We provide mobile services throughout the Greater Houston area.
                    Initial evaluations are conducted in person.
                  </p>
                  <div className="space-y-4">
                    <OptionCard
                      icon={MapPin}
                      title="Houston Area"
                      description="Harris, Fort Bend, or Montgomery County"
                      value="houston_area"
                      fieldKey="visitPreference"
                      testId="option-location-houston"
                    />
                    <OptionCard
                      icon={User}
                      title="Surrounding Area"
                      description="Within driving distance of Greater Houston"
                      value="surrounding"
                      fieldKey="visitPreference"
                      testId="option-location-surrounding"
                    />
                    <OptionCard
                      icon={HelpCircle}
                      title="I'm Not Sure"
                      description="I'd like to discuss service area options"
                      value="not_sure"
                      fieldKey="visitPreference"
                      testId="option-location-unsure"
                    />
                  </div>
                  
                  {/* In-person evaluation note */}
                  <div className="mt-6 p-4 bg-teal-50 border border-teal-200 rounded-lg">
                    <p className="text-sm text-teal-800">
                      <strong>Note:</strong> Initial evaluations are performed in person at your 
                      location or an appropriate clinical setting. Virtual follow-up appointments 
                      may be available for established patients when clinically appropriate.
                    </p>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
                {step > 1 ? (
                  <button
                    onClick={goBack}
                    className="btn-secondary flex items-center gap-2"
                    data-testid="btn-back"
                  >
                    <ArrowLeft size={18} />
                    Back
                  </button>
                ) : (
                  <div />
                )}
                
                {step === 5 && answers.visitPreference && (
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="btn-accent flex items-center gap-2"
                    data-testid="btn-next-steps"
                  >
                    See Next Steps
                    <ArrowRight size={18} />
                  </button>
                )}
              </div>
            </div>

            {/* Summary (show after all steps) */}
            {step === 5 && answers.visitPreference && (
              <div className="mt-6 bg-teal-50 rounded-xl border border-teal-200 p-6">
                <h3 className="font-semibold text-teal-900 mb-4">Your Summary</h3>
                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-teal-700">You are:</dt>
                    <dd className="font-medium text-teal-900 capitalize">{answers.userType.replace('_', ' ')}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-teal-700">Looking for:</dt>
                    <dd className="font-medium text-teal-900 capitalize">{answers.lookingFor.replace(/_/g, ' ')}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-teal-700">Payment:</dt>
                    <dd className="font-medium text-teal-900 capitalize">{answers.insurance.replace(/_/g, ' ')}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-teal-700">Referral:</dt>
                    <dd className="font-medium text-teal-900 capitalize">{answers.referral.replace(/_/g, ' ')}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-teal-700">Location:</dt>
                    <dd className="font-medium text-teal-900 capitalize">{answers.visitPreference.replace(/_/g, ' ')}</dd>
                  </div>
                </dl>
                
                <p className="text-xs text-teal-700 mt-4 pt-3 border-t border-teal-200">
                  Your initial evaluation will be scheduled as an in-person visit.
                </p>
                
                <button 
                  onClick={resetForm}
                  className="mt-4 text-sm text-teal-700 hover:text-teal-800 underline"
                  data-testid="btn-start-over"
                >
                  Start Over
                </button>
              </div>
            )}

            {/* Healthcare Provider Redirect */}
            {answers.userType === 'provider' && step === 1 && (
              <div className="mt-6 bg-amber-50 rounded-xl border border-amber-200 p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-2">Healthcare Provider?</h3>
                    <p className="text-sm text-amber-800 mb-3">
                      If you're a healthcare provider looking to refer a patient, we have a dedicated 
                      referral process for you.
                    </p>
                    <Link 
                      to="/provider-referrals"
                      className="btn-primary text-sm py-2"
                      data-testid="link-provider-referrals"
                    >
                      Go to Provider Referrals
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* PHI Notice */}
            <div className="mt-6 p-4 bg-slate-100 rounded-lg">
              <p className="text-xs text-slate-600 text-center">
                <strong>Privacy Notice:</strong> Please do not submit detailed medical information, 
                diagnoses, or other sensitive health data through this form. 
                This information should only be shared through approved healthcare channels.
              </p>
            </div>

          </div>
        </div>
      </section>

      <BookingModal 
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        defaultService={answers.lookingFor}
        defaultPaymentType={answers.insurance === 'insurance' ? 'insurance' : answers.insurance === 'self_pay' ? 'self-pay' : 'not_sure'}
      />
    </div>
  );
};

export default StartHerePage;
